<template>
  <div class="section-container">
    <div class="section-header">
      <h1 class="section-title">{{ student.name }} - 考試成績管理</h1>
      <div class="table-controls">
        <input
          type="text"
          v-model="filterText"
          placeholder="搜索考試..."
          class="search-input"
        />
      </div>
    </div>

    <!-- 已參加的考試表格 -->
    <div class="exam-section">
      <h3 class="section-subtitle">已參加的考試 ({{ enrolledExams.length }})</h3>
      <div class="table-container">
        <table class="data-table enrolled-exams">
          <thead>
            <tr>
              <th>考試名稱</th>
              <th>考試機構</th>
              <th>考試類型</th>
              <th>科目</th>
              <th>年級</th>
              <th>考試日期</th>
              <th>分數</th>
              <th>百分比</th>
              <th>排名</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="item in enrolledExams" 
              :key="item.exam.exam_id"
              class="clickable-row enrolled-row"
              @click="goToExam(item.exam.exam_id, $event)"
            >
              <td>{{ item.exam.name }}</td>
              <td>
                <span :class="`authority-badge ${item.exam.authority.toLowerCase()}`">
                  {{ getAuthorityLabel(item.exam.authority) }}
                </span>
              </td>
              <td>
                <span class="type-tag">{{ getTypeLabel(item.exam.type) }}</span>
              </td>
              <td>
                <span class="subject-tag">{{ getSubjectLabel(item.exam.subject) }}</span>
              </td>
              <td>
                <span class="grade-tag">{{ getGradeLabel(item.exam.grade) }}</span>
              </td>
              <td>{{ formatDate(item.exam.exam_date) }}</td>
              <td>
                <span :class="getScoreClass(item.score)">
                  {{ item.score || '-' }}
                </span>
              </td>
              <td>
                <span v-if="item.percentage" class="percentage">
                  {{ item.percentage }}%
                </span>
                <span v-else>-</span>
              </td>
              <td>
                <span v-if="item.rank" class="rank-badge">
                  第{{ item.rank }}名
                </span>
                <span v-else>-</span>
              </td>
              <td class="actions" @click.stop>
                <button class="button button-secondary" @click="editScore(item)">編輯</button>
                <button class="button button-danger" @click="removeScore(item)">移除</button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="enrolledExams.length === 0" class="empty-state">
          尚未參加任何考試
        </div>
      </div>
    </div>

    <!-- 所有其他考試表格 -->
    <div class="exam-section">
      <h3 class="section-subtitle">所有考試 ({{ availableExams.length }})</h3>
      <div class="table-container">
        <table class="data-table available-exams">
          <thead>
            <tr>
              <th>考試名稱</th>
              <th>考試機構</th>
              <th>考試類型</th>
              <th>科目</th>
              <th>年級</th>
              <th>考試日期</th>
              <th>總分</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="exam in availableExams" 
              :key="exam.exam_id"
              class="clickable-row available-row"
              @click="goToExam(exam.exam_id, $event)"
            >
              <td>{{ exam.name }}</td>
              <td>
                <span :class="`authority-badge ${exam.authority.toLowerCase()}`">
                  {{ getAuthorityLabel(exam.authority) }}
                </span>
              </td>
              <td>
                <span class="type-tag">{{ getTypeLabel(exam.type) }}</span>
              </td>
              <td>
                <span class="subject-tag">{{ getSubjectLabel(exam.subject) }}</span>
              </td>
              <td>
                <span class="grade-tag">{{ getGradeLabel(exam.grade) }}</span>
              </td>
              <td>{{ formatDate(exam.exam_date) }}</td>
              <td>{{ exam.full_score || '-' }}</td>
              <td class="actions" @click.stop>
                <button class="button button-primary" @click="addScore(exam)">新增成績</button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="availableExams.length === 0" class="empty-state">
          沒有其他可用的考試
        </div>
      </div>
    </div>

    <!-- 考試成績模態框 -->
    <ExamScoreModal
      v-if="showScoreModal"
      :score="currentScore"
      :exam="currentExam"
      :isEdit="isEditMode"
      :student="student"
      @close="closeScoreModal"
      @saved="handleScoreSaved"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useQuery, useMutation } from '@vue/apollo-composable';
import { 
  GET_EXAMS, 
  DELETE_STUDENT_EXAM_SCORE 
} from '../graphql/queries';
import ExamScoreModal from './ExamScoreModal.vue';

const props = defineProps({
  student: {
    type: Object,
    required: true
  }
});

const router = useRouter();

// GraphQL queries and mutations
const { result: examsResult, refetch: refetchExams } = useQuery(GET_EXAMS);
const { mutate: deleteScore } = useMutation(DELETE_STUDENT_EXAM_SCORE);

// State
const filterText = ref('');
const showScoreModal = ref(false);
const currentScore = ref(null);
const currentExam = ref(null);
const isEditMode = ref(false);

// Computed properties
const exams = computed(() => examsResult.value?.exams || []);

// 學生已參加的考試（有成績記錄的）
const enrolledExams = computed(() => {
  const studentIdStr = props.student?.student_id?.toString();
  if(!studentIdStr || !exams.value || exams.value.length === 0) return [];
  return exams.value
    .map(exam => {
      const scoreRecord = exam.student_scores?.find(
        score => score.student?.student_id?.toString() === studentIdStr
      );
      return scoreRecord ? { exam, ...scoreRecord } : null;
    })
    .filter(Boolean)
    .filter(item => 
      item.exam.name.toLowerCase().includes(filterText.value.toLowerCase()) ||
      getAuthorityLabel(item.exam.authority).toLowerCase().includes(filterText.value.toLowerCase()) ||
      getTypeLabel(item.exam.type).toLowerCase().includes(filterText.value.toLowerCase()) ||
      getSubjectLabel(item.exam.subject).toLowerCase().includes(filterText.value.toLowerCase())
    );
});

// 所有其他可用的考試（學生尚未參加的）
const availableExams = computed(() => {
  const enrolledExamIds = new Set(enrolledExams.value.map(item => item.exam.exam_id));
  return exams.value
    .filter(exam => !enrolledExamIds.has(exam.exam_id))
    .filter(exam => 
      exam.name.toLowerCase().includes(filterText.value.toLowerCase()) ||
      getAuthorityLabel(exam.authority).toLowerCase().includes(filterText.value.toLowerCase()) ||
      getTypeLabel(exam.type).toLowerCase().includes(filterText.value.toLowerCase()) ||
      getSubjectLabel(exam.subject).toLowerCase().includes(filterText.value.toLowerCase())
    );
});

// Labels for enums (與 ExamTable.vue 保持一致)
const authorityLabels = {
  SCHOOL: '學校',
  MACADEMY: '補習班',
  OTHER: '其他'
};

const typeLabels = {
  MIDTERM: '期中考',
  FINAL: '期末考',
  WEEKLY: '週考',
  FIRST_TERM: '第一次段考',
  SECOND_TERM: '第二次段考',
  THIRD_TERM: '第三次段考',
  QUIZ: '隨堂考',
  MOCK: '模擬考',
  MONTHLY: '月考',
  UNIT_TEST: '單元測驗',
  REVIEW_TEST: '複習考',
  PLACEMENT: '分班測驗',
  CERTIFICATION: '證照考試',
  COMPETITION: '競賽'
};

const subjectLabels = {
  CHINESE: '國文',
  CHINESE_ESSAY: '國文作文',
  ENGLISH: '英文',
  ENGLISH_ESSAY: '英文作文',
  ENGLISH_LISTENING: '英文聽力',
  MATH: '數學',
  BIOLOGY: '生物',
  SCIENCE: '理化',
  PHYSICS: '物理',
  CHEMISTRY: '化學',
  HISTORY: '歷史',
  GEOGRAPHY: '地理',
  CIVICS: '公民',
  OTHER: '其他'
};

const getGradeLabels = {
  1: '小一', 2: '小二', 3: '小三', 4: '小四', 5: '小五', 6: '小六',
  7: '國一', 8: '國二', 9: '國三', 10: '高一', 11: '高二', 12: '高三'
};

// Helper functions
function getAuthorityLabel(authority) {
  return authorityLabels[authority] || authority;
}

function getTypeLabel(type) {
  return typeLabels[type] || type;
}

function getSubjectLabel(subject) {
  return subjectLabels[subject] || subject;
}

function getGradeLabel(grade) {
  return getGradeLabels[grade] || (grade ? ` ${grade}年級` : '-');
}

function formatDate(dateString) {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('zh-TW');
}

function getScoreClass(score) {
  if (score === null || score === undefined) return 'score-missing';
  if (score >= 90) return 'score-excellent';
  if (score >= 80) return 'score-good';
  if (score >= 60) return 'score-average';
  return 'score-poor';
}

function goToExam(examId, event) {
  const isButtonClick = event.target.closest('button') !== null;
  if(!isButtonClick) {
    router.push(`/exams/${examId}`);
  }
}  

// Modal functions
function addScore(exam) {
  currentExam.value = exam;
  currentScore.value = null;
  isEditMode.value = false;
  showScoreModal.value = true;
}

function editScore(scoreItem) {
  currentExam.value = scoreItem.exam;
  currentScore.value = scoreItem;
  isEditMode.value = true;
  showScoreModal.value = true;
}

function closeScoreModal() {
  showScoreModal.value = false;
  currentScore.value = null;
  currentExam.value = null;
}

function handleScoreSaved() {
  refetchExams();
  closeScoreModal();
}

async function removeScore(scoreItem) {
  if (confirm('確定要移除這個考試成績嗎？')) {
    try {
      await deleteScore({ id: scoreItem.id });
      refetchExams();
    } catch (error) {
      console.error('移除成績失敗:', error);
      alert('移除成績失敗: ' + error.message);
    }
  }
}

onMounted(() => {
  refetchExams();
});
</script>

<style scoped>
.exam-section {
  margin-bottom: 2rem;
}

.section-subtitle {
  margin: 0 0 1rem 0;
  font-size: 1.2rem;
  color: var(--text-dark);
  border-bottom: 2px solid var(--primary-color);
  padding-bottom: 0.5rem;
}

/* 表格樣式 */
.enrolled-exams tbody tr {
  background-color: #f8fff8;
}

.enrolled-exams tbody tr:hover {
  background-color: #f0fff0;
}

.available-exams tbody tr:hover {
  background-color: var(--light-bg);
}

/* 分數樣式 */
.score-excellent {
  color: #4caf50;
  font-weight: bold;
}

.score-good {
  color: #2196f3;
  font-weight: bold;
}

.score-average {
  color: #ff9800;
}

.score-poor {
  color: #f44336;
}

.score-missing {
  color: var(--text-light);
  font-style: italic;
}

/* 排名徽章 */
.rank-badge {
  background-color: #e3f2fd;
  color: #1976d2;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: bold;
}

/* 百分比樣式 */
.percentage {
  font-weight: bold;
  color: var(--text-dark);
}

.clickable-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

/* 操作按鈕 */
.actions {
  display: flex;
  gap: 8px;
  cursor: default;
}

.button {
  padding: 0.375rem 0.8rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.button-primary {
  background-color: var(--primary-color);
  color: white;
}

.button-primary:hover {
  background-color: var(--primary-hover);
}

.button-secondary {
  background-color: var(--secondary-color);
  color: white;
}

.button-secondary:hover {
  background-color: #1976D2;
}

.button-danger {
  background-color: var(--danger-color);
  color: white;
}

.button-danger:hover {
  background-color: var(--danger-hover);
}

/* 徽章和標籤樣式 */
.authority-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  color: white;
}

.authority-badge.school {
  background-color: var(--primary-color);
}

.authority-badge.macademy {
  background-color: var(--secondary-color);
}

.authority-badge.other {
  background-color: var(--text-light);
}

.type-tag, .subject-tag, .grade-tag {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  background-color: #e3f2fd;
  color: #1976d2;
  border: 1px solid #bbdefb;
}

.grade-tag {
  background-color: #e8f5e8;
  color: #2e7d32;
  border-color: #c8e6c9;
}

/* 空狀態 */
.empty-state {
  padding: 2rem;
  text-align: center;
  color: var(--text-medium);
  background-color: var(--light-bg);
  border-radius: 4px;
  margin: 1rem 0;
}

/* 響應式設計 */
@media (max-width: 768px) {
  .table-container {
    overflow-x: auto;
  }
  
  .actions {
    flex-direction: column;
  }
  
  .button {
    font-size: 0.7rem;
    padding: 0.25rem 0.5rem;
  }
}
</style>